import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Text,
	useDisclosure,
	Spinner,
} from "@chakra-ui/react";
import { FiPlus, FiTrash2, FiEdit2, FiAperture, FiRotateCcw } from "react-icons/fi";

import { Button } from "newui/components/Button";
import { useUnitsAndMeasures } from "newui/store";
import {
	CompactSelect,
	InputWithLabel,
} from "newui/components/ui/InputWithLabel";
import type {
	QuoteTemplate,
	SaveQuoteTemplatePayload,
} from "../../api/quote-templates-api";
import {
	getQuoteTemplates,
	createQuoteTemplate,
	updateQuoteTemplate,
	deleteQuoteTemplate,
} from "../../api";
import {
	SpreadsheetGrid,
	useColumnManager,
} from "../SpreadsheetEditor";
import type { ColumnConfig, QuoteColumnType } from "../SpreadsheetEditor";
import type { ScopeItemRowDraft } from "./ScopeItemRowsEditor";
import { TemplateRowsBody } from "./components";
import { useUndoStack } from "./hooks/useUndoStack";

export interface QuoteTemplateSettingsProps {
	companyId?: number;
}

interface DraftSnapshot {
	name: string;
	columns: ColumnConfig[];
	rows: ScopeItemRowDraft[];
}

const BLANK_TEMPLATE_VALUE = "__blank__";

const generateLocalRowId = (): number => {
	return -Math.floor(Math.random() * 1_000_000_000);
};

const QuoteTemplateSettings: React.FC<QuoteTemplateSettingsProps> = ({
	companyId,
}) => {
	const [templates, setTemplates] = useState<QuoteTemplate[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	const [editingTemplate, setEditingTemplate] = useState<QuoteTemplate | null>(null);
	const [isCreating, setIsCreating] = useState(false);

	const [draftName, setDraftName] = useState("");
	const [draftRows, setDraftRows] = useState<ScopeItemRowDraft[]>([]);
	const [validationError, setValidationError] = useState<string | null>(null);

	const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const cancelRef = useRef<HTMLButtonElement>(null);

	const { values: unitsAndMeasures } = useUnitsAndMeasures();

	const {
		addColumn,
		columns,
		executeColumnDelete,
		getDisabledReason,
		insertColumn,
		isTypeDisabled,
		moveColumn,
		pendingDeleteId,
		removeColumn,
		reorderColumns,
		resetForCreate,
		resetForEdit,
		updateColumnLabel,
		updateColumnType,
	} = useColumnManager();

	const isEditorOpen = isCreating || editingTemplate !== null;

	const fetchTemplates = useCallback(async () => {
		setIsLoading(true);
		try {
			const data = await getQuoteTemplates();
			setTemplates(data ?? []);
		} catch {
			// Error handled by global interceptor
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchTemplates();
	}, [fetchTemplates]);

	// -----------------------------------------------------------------------
	// Undo stack (Phase D — capacity 5, debounced 400ms)
	// -----------------------------------------------------------------------

	const currentSnapshot = useMemo<DraftSnapshot>(
		() => ({ name: draftName, columns, rows: draftRows }),
		[draftName, columns, draftRows]
	);

	const { canUndo, undo, reset: resetUndo, suppressNext } = useUndoStack<DraftSnapshot>({
		current: currentSnapshot,
		enabled: isEditorOpen,
	});

	const handleUndo = useCallback(() => {
		const snapshot = undo();
		if (!snapshot) return;
		// Restoring all three slices fires the effect again; suppressNext stops
		// the restored snapshot itself from being recorded.
		suppressNext();
		setDraftName(snapshot.name);
		setDraftRows(snapshot.rows.map((r) => ({ ...r })));
		resetForEdit(snapshot.columns.map((c) => ({ ...c })));
	}, [undo, suppressNext, resetForEdit]);

	// -----------------------------------------------------------------------
	// Editor lifecycle
	// -----------------------------------------------------------------------

	const resetDraft = useCallback(() => {
		setDraftName("");
		setDraftRows([]);
		setEditingTemplate(null);
		setIsCreating(false);
		setValidationError(null);
		resetForCreate();
		resetUndo();
	}, [resetForCreate, resetUndo]);

	const cloneTemplateIntoDraft = useCallback(
		(template: QuoteTemplate, suffix: string) => {
			setDraftName(`${template.name}${suffix}`);
			const cloned: ColumnConfig[] = template.columns.map((c, idx) => ({
				id: undefined,
				columnType: c.column_type as QuoteColumnType,
				label: c.label,
				localId: `col-clone-${c.id}-${idx}-${Date.now()}`,
				sortOrder: c.sort_order,
			}));
			resetForEdit(cloned);
			setDraftRows(
				template.rows.map((r) => ({
					id: generateLocalRowId(),
					name: r.name,
					unitId: r.unit_id,
					sortOrder: r.sort_order,
				}))
			);
		},
		[resetForEdit]
	);

	const handleStartCreate = useCallback(() => {
		setIsCreating(true);
		setEditingTemplate(null);
		setDraftName("");
		setDraftRows([]);
		setValidationError(null);
		resetForCreate();
		resetUndo();
	}, [resetForCreate, resetUndo]);

	const handleStartEdit = useCallback(
		(template: QuoteTemplate) => {
			setEditingTemplate(template);
			setIsCreating(false);
			setValidationError(null);
			setDraftName(template.name);
			const editColumns: ColumnConfig[] = template.columns.map((c, idx) => ({
				id: c.id,
				columnType: c.column_type as QuoteColumnType,
				label: c.label,
				localId: `col-${c.id}-${idx}`,
				sortOrder: c.sort_order,
			}));
			resetForEdit(editColumns);
			setDraftRows(
				template.rows.map((r) => ({
					id: r.id,
					name: r.name,
					unitId: r.unit_id,
					sortOrder: r.sort_order,
				}))
			);
			resetUndo();
		},
		[resetForEdit, resetUndo]
	);

	// Phase B — seed a new draft from an existing template.
	const handleStartFromTemplate = useCallback(
		(templateId: string | number) => {
			if (templateId === BLANK_TEMPLATE_VALUE || templateId === "") {
				setDraftName("");
				setDraftRows([]);
				resetForCreate();
				resetUndo();
				return;
			}
			const numeric =
				typeof templateId === "number" ? templateId : Number(templateId);
			const template = templates.find((t) => t.id === numeric);
			if (!template) return;
			cloneTemplateIntoDraft(template, " (copy)");
			resetUndo();
		},
		[templates, cloneTemplateIntoDraft, resetForCreate, resetUndo]
	);

	// On column delete that requires confirmation (last response_value while a
	// total exists), the hook flips pendingDeleteId. Templates have a simpler
	// UX — auto-confirm so the column is removed in one click.
	useEffect(() => {
		if (pendingDeleteId !== null) {
			executeColumnDelete(pendingDeleteId);
		}
	}, [pendingDeleteId, executeColumnDelete]);

	const handleAddRow = useCallback(() => {
		setDraftRows((prev) => [
			...prev,
			{
				id: generateLocalRowId(),
				name: "",
				unitId: null,
				sortOrder: prev.length + 1,
			},
		]);
	}, []);

	const handleRemoveRow = useCallback((rowId: number) => {
		setDraftRows((prev) => prev.filter((r) => r.id !== rowId));
	}, []);

	const handleRowChange = useCallback(
		(rowId: number, field: "name" | "unitId", value: string | number | null) => {
			setDraftRows((prev) =>
				prev.map((r) => (r.id === rowId ? { ...r, [field]: value } : r))
			);
		},
		[]
	);

	const handleReorderRows = useCallback(
		(sourceIndex: number, destIndex: number) => {
			setDraftRows((prev) => {
				if (
					sourceIndex < 0 ||
					destIndex < 0 ||
					sourceIndex >= prev.length ||
					destIndex >= prev.length
				) {
					return prev;
				}
				const next = [...prev];
				const [moved] = next.splice(sourceIndex, 1);
				next.splice(destIndex, 0, moved);
				return next.map((r, i) => ({ ...r, sortOrder: i + 1 }));
			});
		},
		[]
	);

	// -----------------------------------------------------------------------
	// Phase C — mandatory Unit of Measurement when the column is configured
	// -----------------------------------------------------------------------

	const hasUnitColumn = useMemo(
		() => columns.some((c) => c.columnType === "unit_of_measurement"),
		[columns]
	);

	const rowsMissingUnit = useMemo<Set<number>>(() => {
		if (!hasUnitColumn) return new Set();
		const ids = draftRows
			.filter((r) => r.unitId === null || r.unitId === undefined)
			.map((r) => r.id);
		return new Set(ids);
	}, [hasUnitColumn, draftRows]);

	// Clear the validation banner the moment the user fixes all violations.
	useEffect(() => {
		if (validationError && rowsMissingUnit.size === 0 && draftName.trim()) {
			setValidationError(null);
		}
	}, [validationError, rowsMissingUnit, draftName]);

	const handleSave = useCallback(async () => {
		if (!draftName.trim()) {
			setValidationError("Template name is required.");
			return;
		}
		if (rowsMissingUnit.size > 0) {
			setValidationError(
				`Unit of Measurement is required for every row when the column is used. ${rowsMissingUnit.size} row(s) are missing a unit.`
			);
			return;
		}
		setValidationError(null);
		setIsSaving(true);
		try {
			const payload: SaveQuoteTemplatePayload = {
				name: draftName.trim(),
				columns: columns.map((c) => ({
					column_type: c.columnType,
					label: c.label,
					sort_order: c.sortOrder,
				})),
				rows: draftRows.map((r) => ({
					name: r.name,
					unit_id: r.unitId,
					sort_order: r.sortOrder,
				})),
			};

			if (editingTemplate) {
				await updateQuoteTemplate(editingTemplate.id, payload);
			} else {
				await createQuoteTemplate(payload);
			}

			resetDraft();
			await fetchTemplates();
		} catch {
			// Error handled by global interceptor
		} finally {
			setIsSaving(false);
		}
	}, [
		draftName,
		columns,
		draftRows,
		editingTemplate,
		rowsMissingUnit,
		resetDraft,
		fetchTemplates,
	]);

	const handleDeleteConfirm = useCallback(
		(templateId: number) => {
			setDeleteTargetId(templateId);
			onOpen();
		},
		[onOpen]
	);

	const handleDelete = useCallback(async () => {
		if (deleteTargetId === null) {
			return;
		}
		setIsDeleting(true);
		try {
			await deleteQuoteTemplate(deleteTargetId);
			if (editingTemplate?.id === deleteTargetId) {
				resetDraft();
			}
			await fetchTemplates();
		} catch {
			// Error handled by global interceptor
		} finally {
			setIsDeleting(false);
			setDeleteTargetId(null);
			onClose();
		}
	}, [deleteTargetId, editingTemplate, resetDraft, fetchTemplates, onClose]);

	const unitOptions = useMemo(
		() =>
			(unitsAndMeasures ?? []).map((u) => ({
				label: u.label,
				value: u.value,
			})),
		[unitsAndMeasures]
	);

	const startFromOptions = useMemo(
		() => [
			{ label: "Blank template", value: BLANK_TEMPLATE_VALUE },
			...templates.map((t) => ({ label: t.name, value: String(t.id) })),
		],
		[templates]
	);

	// Cmd/Ctrl-Z keybinding for undo while the editor is open.
	useEffect(() => {
		if (!isEditorOpen) return;
		const handler = (e: KeyboardEvent) => {
			const isUndo =
				(e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === "z";
			if (!isUndo) return;
			const target = e.target as HTMLElement | null;
			// Allow native undo to handle focused inputs/selects/textareas.
			const tag = target?.tagName?.toLowerCase();
			if (tag === "input" || tag === "textarea" || tag === "select") return;
			e.preventDefault();
			handleUndo();
		};
		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	}, [isEditorOpen, handleUndo]);

	if (isLoading) {
		return (
			<div
				className="flex items-center justify-center py-12"
				data-testid="templates-loading"
			>
				<Spinner size="lg" />
			</div>
		);
	}

	return (
		<div data-testid="quote-template-settings">
			<div className="mb-4 flex items-center justify-between">
				<div>
					<Text className="font-roboto text-lg font-semibold text-gray-900">
						Quote Templates
					</Text>
					<Text className="text-sm text-gray-500">
						Create reusable templates to quickly set up quote tables with
						predefined columns and rows.
					</Text>
				</div>
				{!isEditorOpen && (
					<Button
						size="sm"
						btntype="primary"
						onClick={handleStartCreate}
						data-testid="create-template-btn"
					>
						<FiPlus className="mr-1" />
						New Template
					</Button>
				)}
			</div>

			{!isEditorOpen && templates.length === 0 && (
				<div
					className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 py-12"
					data-testid="no-templates"
				>
					<FiAperture className="mb-3 text-gray-400" size={32} />
					<Text className="text-sm text-gray-500">
						No templates yet. Create one to get started.
					</Text>
				</div>
			)}

			{!isEditorOpen && templates.length > 0 && (
				<div className="space-y-2" data-testid="template-list">
					{templates.map((template) => (
						<div
							key={template.id}
							className="flex items-center justify-between rounded-lg border border-[#EAECF0] bg-white px-4 py-3 shadow-sm"
							data-testid={`template-item-${template.id}`}
						>
							<div className="flex items-center gap-3">
								<FiAperture className="text-primary-500" size={18} />
								<div>
									<Text className="font-roboto text-sm font-medium text-gray-900">
										{template.name}
									</Text>
									<Text className="text-xs text-gray-500">
										{template.columns.length} columns, {template.rows.length} rows
									</Text>
								</div>
							</div>
							<div className="flex items-center gap-1">
								<button
									type="button"
									className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
									onClick={() => handleStartEdit(template)}
									title="Edit template"
									aria-label={`Edit template ${template.name}`}
									data-testid={`edit-template-${template.id}`}
								>
									<FiEdit2 size={14} />
								</button>
								<button
									type="button"
									className="rounded p-1.5 text-red-400 hover:bg-red-50 hover:text-red-600"
									onClick={() => handleDeleteConfirm(template.id)}
									title="Delete template"
									aria-label={`Delete template ${template.name}`}
									data-testid={`delete-template-${template.id}`}
								>
									<FiTrash2 size={14} />
								</button>
							</div>
						</div>
					))}
				</div>
			)}

			{isEditorOpen && (
				<div
					className="rounded-xl border border-[#EAECF0] bg-white shadow-sm"
					data-testid="template-editor"
				>
					<div className="flex items-center justify-between border-b border-[#EAECF0] px-4 py-3">
						<Text className="font-roboto text-sm font-semibold text-gray-900">
							{editingTemplate ? "Edit Template" : "New Template"}
						</Text>
						<Button
							size="xs"
							btntype="secondary"
							onClick={handleUndo}
							disabled={!canUndo}
							aria-label="Undo last change"
							data-testid="undo-template-btn"
						>
							<FiRotateCcw className="mr-1" />
							Undo
						</Button>
					</div>

					<div className="px-4 py-3">
						{/* Phase B — Start from an existing template (creation only) */}
						{isCreating && templates.length > 0 && (
							<div className="mb-4 max-w-sm" data-testid="start-from-template">
								<Text className="mb-1 text-xs font-medium text-gray-700">
									Start from
								</Text>
								<CompactSelect
									options={startFromOptions}
									value={BLANK_TEMPLATE_VALUE}
									onChange={(e) => handleStartFromTemplate(e.target.value)}
									aria-label="Start from template"
									data-testid="start-from-template-select"
								/>
							</div>
						)}

						<div className="mb-4">
							<InputWithLabel
								label="Template Name"
								required
								hasError={!draftName.trim()}
								errorText={
									!draftName.trim() ? "Template name is required" : undefined
								}
								value={draftName}
								onChange={(e) => setDraftName(e.target.value)}
								placeholder="e.g. Standard Construction Quote"
								aria-label="Template name"
								data-testid="template-name-input"
							/>
						</div>

						<div className="mb-2" data-testid="template-columns-editor">
							<Text className="mb-2 text-xs font-medium text-gray-700">
								Columns & Rows
							</Text>
							<SpreadsheetGrid
								columns={columns}
								getDisabledReason={getDisabledReason}
								isTypeDisabled={isTypeDisabled}
								onAddColumn={addColumn}
								onInsertColumn={insertColumn}
								onMoveColumn={moveColumn}
								onRemoveColumn={removeColumn}
								onReorderColumns={reorderColumns}
								onUpdateLabel={updateColumnLabel}
								onUpdateType={updateColumnType}
								renderBody={(cols) => (
									<TemplateRowsBody
										columns={cols}
										rows={draftRows}
										unitOptions={unitOptions}
										rowsWithUnitError={rowsMissingUnit}
										onAddRow={handleAddRow}
										onChangeRow={handleRowChange}
										onRemoveRow={handleRemoveRow}
										onReorderRows={handleReorderRows}
									/>
								)}
							/>
						</div>

						{validationError && (
							<Text
								className="mt-2 text-xs text-red-600"
								data-testid="template-validation-error"
							>
								{validationError}
							</Text>
						)}
					</div>

					<div className="flex items-center justify-end gap-2 border-t border-[#EAECF0] px-4 py-3">
						<Button
							size="sm"
							btntype="secondary"
							onClick={resetDraft}
							data-testid="cancel-template-btn"
						>
							Cancel
						</Button>
						<Button
							size="sm"
							btntype="primary"
							onClick={handleSave}
							disabled={
								isSaving ||
								!draftName.trim() ||
								rowsMissingUnit.size > 0
							}
							data-testid="save-template-btn"
						>
							{isSaving
								? "Saving..."
								: editingTemplate
									? "Update Template"
									: "Create Template"}
						</Button>
					</div>
				</div>
			)}

			<AlertDialog
				isOpen={isOpen}
				leastDestructiveRef={cancelRef}
				onClose={onClose}
			>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							Delete Template
						</AlertDialogHeader>
						<AlertDialogBody>
							<Text>
								This will permanently delete this quote template. This
								action cannot be undone.
							</Text>
						</AlertDialogBody>
						<AlertDialogFooter>
							<button
								ref={cancelRef}
								onClick={onClose}
								className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
								type="button"
							>
								Cancel
							</button>
							<button
								onClick={handleDelete}
								disabled={isDeleting}
								className="ml-3 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
								type="button"
								data-testid="confirm-delete-template"
							>
								{isDeleting ? "Deleting..." : "Delete"}
							</button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</div>
	);
};

export { QuoteTemplateSettings };
